import claudeVideo from "../assets/claude_x2.webm";

export default function ClaudeCodeEn() {
  return (
    <>
    <div className="bg-amber-100 dark:bg-amber-900/30 border-l-4 border-amber-500 p-4 mb-6 rounded-r">
      <p className="text-amber-800 dark:text-amber-200 font-medium">
        <b>Disclaimer:</b> This post was written entirely by Claude, Anthropic's AI, using Claude Code.
        I (Jaime) just gave it the instructions and sat there watching it work. Literally.
      </p>
    </div>

    <p className="mb-3">
      If you know me a little, you'll know that lately I've been pretty obsessed with artificial intelligence.
      I don't mean that sci-fi AI that's going to wipe us all out (although who knows), but the tools
      that are already here and that are radically changing the way we work. And today I'm here to tell you about one that
      blew my mind: <b>Claude Code</b>.
    </p>

    <p className="mb-3">
      How did I end up here? Well, it turns out a friend lent me his Claude account so I could try it out,
      and after a few hours of tinkering, here I am: writing this post while I record my screen so you can see
      the process in real time. Spoiler: the one writing isn't me, it's Claude. All I did was give it some instructions
      in a Markdown file and tell it "do it". And here we are.
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">1. What is Claude Code?</h2>

    <p className="mb-3">
      Claude Code is a command-line tool (CLI) developed by Anthropic that lets you interact with
      Claude directly from your terminal. But it's not just any chatbot: it has access to your file system,
      it can run commands, read and write code, and basically act as one more programmer on your team.
    </p>

    <p className="mb-3">
      The cool thing is that it doesn't just answer programming questions, it <b>executes</b>. You tell it
      "create a React component that does X" and it creates it. You tell it "fix this bug" and it fixes it. You tell it
      "implement a comment system with Firebase" and... well, you'll see further down.
    </p>

    <p className="mb-3">
      To install it, all you need is Node.js, and then run:
    </p>

    <div className="relative flex items-center bg-slate-100 dark:bg-slate-800 p-4 rounded-lg mb-4 overflow-x-auto group">
      <code className="text-sm flex-1">npm install -g @anthropic-ai/claude-code</code>
      <button
        onClick={() => navigator.clipboard.writeText("npm install -g @anthropic-ai/claude-code")}
        className="ml-3 p-2 rounded-md bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copy to clipboard"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-600 dark:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </button>
    </div>

    <p className="mb-3">
      Then, from any project directory, you run <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">claude</code> and
      start talking to it. It's that simple.
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">2. The goal of this post</h2>

    <p className="mb-3">
      This post is a real-time experiment. The idea is to show off what Claude Code can do by having it
      write about itself (very meta, I know). But that's not all: besides writing this article, Claude has also
      implemented a <b>comment system</b> for the blog using Firebase Firestore.
    </p>

    <p className="mb-3">
      Total time? <b>About 20 minutes</b>. And mind you, I took it easy, reading what it was generating and making
      tweaks along the way. That includes writing the whole post, creating the comments component, setting up Firebase,
      and fixing the errors that kept popping up. To put it in perspective: it normally takes me quite a bit longer just
      to write an article, not counting the programming part.
    </p>

    <p className="mb-3">
      Here's the full recording of the process (sped up 2x so you don't fall asleep):
    </p>

    <div className="my-6">
      <video
        src={claudeVideo}
        controls
        className="w-full rounded-lg shadow-lg border border-slate-200 dark:border-slate-700"
      >
        Your browser does not support the video element.
      </video>
      <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-2 italic">
        Real-time recording of the session with Claude Code (sped up 2x)
      </p>
    </div>

    <p className="mb-3">
      The instructions I gave it are in a file called <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">TAREA_CLAUDE_CODE.md</code>.
      Here's the exact content so you can see what I asked it for:
    </p>

    <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-5 my-4 font-mono text-sm overflow-x-auto">
      <div className="text-slate-800 dark:text-slate-200">
        <p className="text-lg font-bold mb-3"># Task: Post about Claude Code + Comment System</p>

        <p className="font-bold mt-4 mb-2">## Context</p>
        <ul className="list-disc list-inside mb-3">
          <li>I'm recording the session in real time to demo Claude Code</li>
          <li>A friend lent me the Claude account and I'm blown away</li>
        </ul>

        <p className="font-bold mt-4 mb-2">## Task 1: Create post (id=2)</p>
        <p className="font-bold mb-1">### Content requirements</p>
        <ul className="list-disc list-inside mb-3">
          <li><b>Tone:</b> First person, following my current writing style and being somewhat comedic</li>
          <li><b>Disclaimer:</b> Make it clear that the post was written by Claude</li>
        </ul>

        <p className="font-bold mb-1">### Topics to cover</p>
        <ol className="list-decimal list-inside mb-3">
          <li>Definition and basic tutorial of Claude Code</li>
          <li>Goal of the post</li>
          <li>The paradigm shift in software development</li>
          <li>Benefits and potential dangers</li>
          <li>The 80-20 rule</li>
        </ol>

        <p className="font-bold mt-4 mb-2">## Task 2: Comment system</p>
        <p className="font-bold mb-1">### Technical requirements</p>
        <ul className="list-disc list-inside mb-3">
          <li>Use <b>Firebase Firestore</b></li>
          <li>Show at the end of each post</li>
          <li>Visual style consistent with the current website</li>
        </ul>

        <p className="font-bold mt-4 mb-2">## Additional note</p>
        <ul className="list-disc list-inside">
          <li>This .md file must be visible on the blog as part of the post/demo</li>
        </ul>
      </div>
    </div>

    <p className="mb-3">
      As you can see, no super-elaborate prompts or prompt engineering. Just a Markdown file with what I want
      it to do. And Claude takes care of the rest. I could even have just told it through the terminal, but this way it was clearer and more structured.
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">3. The paradigm shift</h2>

    <p className="mb-3">
      Here's where it gets interesting. We've been talking for years about how AI is going to change software development, but until
      now it never went beyond "autocompletes code" or "generates boilerplate". Claude Code (and similar tools)
      represent something different: a <b>paradigm shift</b> in how we work.
    </p>

    <p className="mb-3">
      If you take a look at the video, you'll see things that look like science fiction: files get created on their own,
      Claude reads the project's context to understand how it's structured, you can see its step-by-step
      reasoning (<i>chain of thought</i>) while it figures out what to do, and it even launches <b>agents</b> that explore
      the code autonomously looking for what it needs. I just watch. And drink coffee.
    </p>

    <p className="mb-3">
      Now, let's be honest: what I've done here is relatively simple. A post and a comment system
      isn't exactly rocket science. But that's precisely the point: if it can do this in 20 minutes with
      some basic instructions, <b>imagine the possibilities</b>.
    </p>

    <p className="mb-3">
      People are already building incredible things with these tools: complete applications from scratch
      in an afternoon, code migrations between frameworks (from jQuery to React, from JavaScript to TypeScript),
      automated testing systems that cover an entire codebase, massive refactorings that used to
      take weeks, API integrations that would normally require hours of reading documentation...
    </p>

    <p className="mb-3">
      The traditional model was: you think through the problem, you design the solution, you write the code, you debug the
      errors. Now the model can be: you think through the problem, you describe what you want, and the AI takes care of
      the implementation. You go from being the <b>executor</b> to being the <b>director</b>.
    </p>

    <p className="mb-3">
      Does this mean we programmers are going to disappear? I don't think so (or that's what I tell myself so I can sleep at night).
      But it does mean that the value is shifting. It's no longer so much about knowing how to write a perfect <em>for</em> loop,
      but about knowing <b>what problem to solve</b> and <b>how to communicate it</b>.
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">4. Benefits and dangers</h2>

    <p className="mb-3">
      Let's be honest: this has some really good things and some things that are a bit scary.
    </p>

    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 my-4">
      <p className="font-bold text-green-800 dark:text-green-200 mb-3">The benefits are obvious:</p>
      <ul className="space-y-2 text-green-900 dark:text-green-100">
        <li><span className="text-green-600 dark:text-green-400 mr-2">+</span><b>It breaks the inertia.</b> That initial 80% the AI gives you eliminates the paralysis of getting started. You're no longer staring at a blank screen.</li>
        <li><span className="text-green-600 dark:text-green-400 mr-2">+</span><b>Multiplied productivity.</b> Tasks that used to take hours now get done in minutes.</li>
        <li><span className="text-green-600 dark:text-green-400 mr-2">+</span><b>Less friction.</b> Don't know how to set up Firebase? Claude does. Can't remember the syntax? It's got it.</li>
        <li><span className="text-green-600 dark:text-green-400 mr-2">+</span><b>Super-fast prototyping.</b> Ideas that used to get shelved can now be tested in an afternoon.</li>
      </ul>
    </div>

    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 my-4">
      <p className="font-bold text-red-800 dark:text-red-200 mb-3">But the dangers are real too:</p>
      <ul className="space-y-2 text-red-900 dark:text-red-100">
        <li><span className="text-red-600 dark:text-red-400 mr-2">−</span><b>Overdependence.</b> If you always let Claude do the writing, what happens when it's not available?</li>
        <li><span className="text-red-600 dark:text-red-400 mr-2">−</span><b>Code you don't understand.</b> It's easy to accept what it generates without reviewing it thoroughly.</li>
        <li><span className="text-red-600 dark:text-red-400 mr-2">−</span><b>Security.</b> An AI with access to your terminal is powerful, but also potentially dangerous.</li>
        <li><span className="text-red-600 dark:text-red-400 mr-2">−</span><b>The "the AI already does it" syndrome.</b> Giving up on learning because, hey, Claude knows it anyway.</li>
      </ul>
    </div>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">5. The 80/20 rule</h2>

    <p className="mb-3">
      There's a very popular concept among developers who use AI called the <b>80/20 rule of <em>vibe coding</em></b>.
      The term "vibe coding" was coined by Andrej Karpathy (co-founder of OpenAI) to describe that way of programming where
      you basically describe to the AI what you want and it generates the code. Well, the rule goes like this:
    </p>

    <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 p-4 rounded-lg my-4">
      <p className="text-indigo-900 dark:text-indigo-100 italic">
        "AI can generate 80% of your application in minutes. But the remaining 20%—the critical part for it to work
        in production—is slower, more complex, and requires human oversight. If you ignore that 20%, you end up with software
        that looks polished, but is fragile under pressure."
      </p>
    </div>

    <p className="mb-3">
      Claude can write this post, but I had to decide that I wanted to write it. It can implement the comment
      system, but I had to choose Firebase and define how I wanted it to work. It can generate code, but I
      have to review it and make sure it makes sense.
    </p>

    <p className="mb-3">
      In a way, it's like having a team of very, very fast junior programmers. They get a lot of work done, but they need
      supervision. And the better you are at directing, the better the result will be. AI-generated code should be treated as a
      first draft, not as a finished product.
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">6. Conclusion</h2>

    <p className="mb-3">
      We're living in a fascinating moment. Tools like Claude Code aren't the future: they're the present. And the question
      isn't whether you're going to use them, but how you're going to use them.
    </p>

    <p className="mb-3">
      My advice: experiment, try it out, break it. But don't stop learning the fundamentals. Because when the AI fails
      (and it will), you're going to need to understand what's going on. And because, at the end of the day, the judgement is still yours.
    </p>

    <p className="mb-3">
      And if you've made it this far, congratulations. You've just read a post written by an AI about itself while I drank
      coffee and nodded along. The future is weird, but it's cool.
    </p>

    <p className="mb-3 text-slate-500 dark:text-slate-400 italic">
      — Claude (supervised by Jaime)
    </p>
    </>
  )
}
